import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Configure axios to send credentials with all requests
axios.defaults.withCredentials = true;

// Safari ITP fallback: attach Bearer token from localStorage if cookie is blocked.
// Backend (auth.py) already accepts Authorization header as fallback.
axios.interceptors.request.use((config) => {
  try {
    const token = localStorage.getItem('ua_session_token');
    if (token && !config.headers?.Authorization) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${token}`;
    }
  } catch { /* localStorage not available */ }
  return config;
});

const AppContext = createContext();

export const useApp = () => useContext(AppContext);

export const AppProvider = ({ children }) => {
  // User authentication state
  const [user, setUser] = useState(null);
  const [userRole, setUserRole] = useState(null);
  
  // Dashboard data
  const [kpiData, setKpiData] = useState(null);
  const [revenueData, setRevenueData] = useState([]);
  const [expenseData, setExpenseData] = useState([]);
  const [cashflowData, setCashflowData] = useState([]);
  const [balanceSheet, setBalanceSheet] = useState(null);
  const [monthlySummary, setMonthlySummary] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);

  // Raw data for tables
  const [revenueList, setRevenueList] = useState([]);
  const [expenseList, setExpenseList] = useState([]);
  const [cashflowList, setCashflowList] = useState([]);
  const [balanceSheetList, setBalanceSheetList] = useState([]);
  const [targetBudgetList, setTargetBudgetList] = useState([]);

  // PM Sales data
  const [pmDashboard, setPmDashboard] = useState(null);
  const [pmSalesList, setPmSalesList] = useState([]);
  const [pmLoading, setPmLoading] = useState(false);

  // Staff & Payroll data
  const [staffList, setStaffList] = useState([]);
  const [payrollSummary, setPayrollSummary] = useState(null);
  const [staffLoading, setStaffLoading] = useState(false);

  // UI State
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Set user data and extract role
  const updateUser = useCallback((userData) => {
    setUser(userData);
    setUserRole(userData?.role || null);
  }, []);

  // Check if user has access to a feature
  const hasAccess = useCallback((feature) => {
    if (!userRole) return false;
    
    // Director, Finance, and Viewer (read-only auditor) have view access to everything
    if (userRole === 'director' || userRole === 'finance' || userRole === 'viewer') return true;
    
    // HR only has access to HR-related features
    if (userRole === 'hr') {
      const hrFeatures = ['hr', 'staff', 'hiring', 'staff-analytics', 'panduan'];
      return hrFeatures.includes(feature);
    }
    
    return false;
  }, [userRole]);

  // Fetch dashboard data
  const fetchDashboardData = useCallback(async () => {
    try {
      const [kpiRes, revRes, expRes, cfRes, bsRes, msRes, alertRes] = await Promise.all([
        axios.get(`${API}/dashboard/kpi`).catch(() => ({ data: null })),
        axios.get(`${API}/dashboard/revenue?period=monthly`).catch(() => ({ data: [] })),
        axios.get(`${API}/dashboard/expenses?period=monthly`).catch(() => ({ data: [] })),
        axios.get(`${API}/dashboard/cashflow?period=monthly`).catch(() => ({ data: [] })),
        axios.get(`${API}/dashboard/balance-sheet`).catch(() => ({ data: null })),
        axios.get(`${API}/dashboard/monthly-summary`).catch(() => ({ data: null })),
        axios.get(`${API}/dashboard/alerts`).catch(() => ({ data: [] }))
      ]);
      setKpiData(kpiRes.data);
      setRevenueData(revRes.data || []);
      setExpenseData(expRes.data || []);
      setCashflowData(cfRes.data || []);
      setBalanceSheet(bsRes.data);
      setMonthlySummary(msRes.data);
      setAlerts(alertRes.data || []);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    }
  }, []);

  // Fetch raw data for tables
  const fetchRawData = useCallback(async () => {
    try {
      const [revRes, expRes, cfRes, bsRes, tbRes] = await Promise.all([
        axios.get(`${API}/revenue`).catch(() => ({ data: [] })),
        axios.get(`${API}/expenses`).catch(() => ({ data: [] })),
        axios.get(`${API}/cashflow`).catch(() => ({ data: [] })),
        axios.get(`${API}/balance-sheet-items`).catch(() => ({ data: [] })),
        axios.get(`${API}/target-budget`).catch(() => ({ data: [] }))
      ]);
      setRevenueList(revRes.data || []);
      setExpenseList(expRes.data || []);
      setCashflowList(cfRes.data || []);
      setBalanceSheetList(bsRes.data || []);
      setTargetBudgetList(tbRes.data || []);
    } catch (error) {
      console.error('Error fetching raw data:', error);
    }
  }, []);

  // Fetch PM Sales data
  const fetchPMData = useCallback(async () => {
    try {
      setPmLoading(true);
      const [dashRes, salesRes] = await Promise.all([
        axios.get(`${API}/pm/dashboard`).catch(() => ({ data: null })),
        axios.get(`${API}/pm/sales`).catch(() => ({ data: [] }))
      ]);
      setPmDashboard(dashRes.data);
      setPmSalesList(salesRes.data || []);
    } catch (error) {
      console.error('Error fetching PM data:', error);
    } finally {
      setPmLoading(false);
    }
  }, []);

  // Fetch Staff & Payroll data
  const fetchStaffData = useCallback(async () => {
    try {
      setStaffLoading(true);
      const [staffRes, payrollRes] = await Promise.all([
        axios.get(`${API}/staff`).catch(() => ({ data: [] })),
        axios.get(`${API}/payroll/summary`).catch(() => ({ data: null }))
      ]);
      setStaffList(staffRes.data || []);
      setPayrollSummary(payrollRes.data);
    } catch (error) {
      console.error('Error fetching staff data:', error);
    } finally {
      setStaffLoading(false);
    }
  }, []);

  const fetchAllData = useCallback(async () => {
    setLoading(true);
    await Promise.all([fetchDashboardData(), fetchRawData(), fetchPMData(), fetchStaffData()]);
    setLoading(false);
  }, [fetchDashboardData, fetchRawData, fetchPMData, fetchStaffData]);

  useEffect(() => {
    fetchAllData();
    // Auto refresh every 5 minutes
    const interval = setInterval(fetchAllData, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [fetchAllData]);

  // CRUD Operations - Revenue
  const addRevenue = async (data) => {
    await axios.post(`${API}/revenue`, data);
    await fetchAllData();
  };

  const updateRevenue = async (id, data) => {
    await axios.put(`${API}/revenue/${id}`, data);
    await fetchAllData();
  };

  const deleteRevenue = async (id) => {
    await axios.delete(`${API}/revenue/${id}`);
    await fetchAllData();
  };

  // CRUD Operations - Expense
  const addExpense = async (data) => {
    await axios.post(`${API}/expenses`, data);
    await fetchAllData();
  };

  const updateExpense = async (id, data) => {
    await axios.put(`${API}/expenses/${id}`, data);
    await fetchAllData();
  };

  const deleteExpense = async (id) => {
    await axios.delete(`${API}/expenses/${id}`);
    await fetchAllData();
  };

  // CRUD Operations - Cashflow
  const addCashflow = async (data) => {
    await axios.post(`${API}/cashflow`, data);
    await fetchAllData();
  };

  const updateCashflow = async (id, data) => {
    await axios.put(`${API}/cashflow/${id}`, data);
    await fetchAllData();
  };

  const deleteCashflow = async (id) => {
    await axios.delete(`${API}/cashflow/${id}`);
    await fetchAllData();
  };

  // CRUD Operations - Balance Sheet
  const addBalanceSheet = async (data) => {
    await axios.post(`${API}/balance-sheet`, data);
    await fetchAllData();
  };

  const updateBalanceSheet = async (id, data) => {
    await axios.put(`${API}/balance-sheet/${id}`, data);
    await fetchAllData();
  };

  const deleteBalanceSheet = async (id) => {
    await axios.delete(`${API}/balance-sheet/${id}`);
    await fetchAllData();
  };

  // CRUD Operations - Target Budget
  const addTargetBudget = async (data) => {
    await axios.post(`${API}/target-budget`, data);
    await fetchAllData();
  };

  // PM Sales CRUD
  const addPMSales = async (data) => {
    await axios.post(`${API}/pm/sales`, data);
    await fetchPMData();
  };

  const updatePMSales = async (id, data) => {
    await axios.put(`${API}/pm/sales/${id}`, data);
    await fetchPMData();
  };

  const deletePMSales = async (id) => {
    await axios.delete(`${API}/pm/sales/${id}`);
    await fetchPMData();
  };

  // Staff CRUD
  const addStaff = async (data) => {
    await axios.post(`${API}/staff`, data);
    await fetchStaffData();
  };

  const updateStaff = async (id, data) => {
    await axios.put(`${API}/staff/${id}`, data);
    await fetchStaffData();
  };

  const deleteStaff = async (id) => {
    await axios.delete(`${API}/staff/${id}`);
    await fetchStaffData();
  };

  // AI Analysis
  const runAIAnalysis = async (analysisType) => {
    const response = await axios.post(`${API}/ai/analyze`, { analysis_type: analysisType });
    return response.data;
  };

  const value = {
    // User & Auth
    user, userRole, updateUser, hasAccess,
    // Data
    kpiData, revenueData, expenseData, cashflowData, balanceSheet, monthlySummary, alerts,
    revenueList, expenseList, cashflowList, balanceSheetList, targetBudgetList,
    pmDashboard, pmSalesList, pmLoading,
    staffList, payrollSummary, staffLoading,
    loading,
    // UI State
    sidebarOpen, setSidebarOpen,
    // Actions
    fetchAllData, fetchPMData, fetchStaffData,
    addRevenue, updateRevenue, deleteRevenue,
    addExpense, updateExpense, deleteExpense,
    addCashflow, updateCashflow, deleteCashflow,
    addBalanceSheet, updateBalanceSheet, deleteBalanceSheet,
    addTargetBudget,
    addPMSales, updatePMSales, deletePMSales,
    addStaff, updateStaff, deleteStaff,
    runAIAnalysis,
    API
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};
